import { resolveSiteAppearance, type SiteAppearanceSettings } from './site-appearance-contract';
import { resolveManageScopeTokens } from './manage-appearance-adapter';

/**
 * The same DOM token/attribute lifecycle powers both the regular Studio and
 * standalone management authoring tools. No new state store or appearance
 * schema: all values originate in the existing sanitized resolver.
 */
export function applySiteAppearanceScope(
  root: HTMLElement,
  settings: SiteAppearanceSettings,
  systemDark: boolean,
  scope: 'marketing' | 'manage',
): () => void {
  const { dark, tokens: siteTokens } = resolveSiteAppearance(settings, systemDark);
  const tokens = scope === 'manage' ? resolveManageScopeTokens(settings, systemDark) : siteTokens;
  const original = Object.keys(tokens).map((name) => ({
    name,
    value: root.style.getPropertyValue(name),
    priority: root.style.getPropertyPriority(name),
  }));
  const attributes = {
    'data-site-theme': dark ? 'dark' : 'light',
    'data-site-surface': settings.surface,
    'data-site-nav-style': settings.navStyle,
    'data-site-nav-active': settings.navActive,
    'data-site-button-style': settings.buttonStyle,
    'data-site-content-width': settings.contentWidth,
  };
  const previous = Object.keys(attributes).map((name) => ({
    name,
    value: root.getAttribute(name),
  }));
  for (const [name, value] of Object.entries(tokens)) root.style.setProperty(name, value);
  for (const [name, value] of Object.entries(attributes)) root.setAttribute(name, value);

  return () => {
    for (const { name, value, priority } of original) {
      if (value) root.style.setProperty(name, value, priority);
      else root.style.removeProperty(name);
    }
    for (const { name, value } of previous) {
      if (value === null) root.removeAttribute(name);
      else root.setAttribute(name, value);
    }
  };
}
